import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@jq4race/i18n';
import { Button, InputOTP, InputOTPGroup, InputOTPSlot, Label } from '@jq4race/ui';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

const OTP_LENGTH = 6;
const OTP_SLOT_IDS = Array.from({ length: OTP_LENGTH }, (_, i) => `otp-slot-${i}`);

import { useAuthServices } from '../auth-services-context';
import { useAuthState } from '../auth-state-context';
import { AuthShell } from '../components/AuthShell';
import { EmailOtpForm } from '../components/EmailOtpForm';
import { ErrorBanner } from '../components/ErrorBanner';
import { MfaQrCode } from '../components/MfaQrCode';

function buildCodeSchema(t: (key: string) => string) {
  return z.object({
    code: z.string().regex(/^\d{6}$/, t('setupMfa.codeValidation')),
  });
}
type CodeFormValues = z.infer<ReturnType<typeof buildCodeSchema>>;

export function SetupMfaPage() {
  const { t } = useTranslation('auth');
  const auth = useAuthServices();
  const { getMfaIntent, setSession } = useAuthState();
  const [intent] = useState(() => getMfaIntent());
  const navigate = useNavigate();
  const [error, setError] = useState<unknown>(null);
  const [qr, setQr] = useState<{ otpauthUri: string; secret: string } | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [mode, setMode] = useState<'setup' | 'email'>('setup');

  const codeSchema = useMemo(() => buildCodeSchema(t), [t]);
  const form = useForm<CodeFormValues>({
    resolver: zodResolver(codeSchema),
    defaultValues: { code: '' },
  });

  useEffect(() => {
    if (!intent) return;
    let cancelled = false;
    auth
      .setupMfa(intent.mfaToken)
      .then((res) => {
        if (!cancelled) setQr(res);
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      });
    return () => {
      cancelled = true;
    };
  }, [auth, intent]);

  async function onSubmit(values: CodeFormValues) {
    if (!intent) return;
    setError(null);
    try {
      const result = await auth.activateMfa({
        mfaToken: intent.mfaToken,
        code: values.code,
        email: intent.email,
      });
      setSession(result.session);
      setRecoveryCodes(result.recoveryCodes);
    } catch (err) {
      setError(err);
    }
  }

  if (recoveryCodes) {
    return (
      <AuthShell
        title={t('setupMfa.recovery.title')}
        eyebrow={t('setupMfa.recovery.eyebrow')}
        description={t('setupMfa.recovery.description')}
        aside={{
          illustration: 'padlock',
          eyebrow: t('setupMfa.recovery.aside.eyebrow'),
          headline: t('setupMfa.recovery.aside.headline'),
          body: t('setupMfa.recovery.aside.body'),
        }}
      >
        <ul className="grid grid-cols-1 gap-2 rounded-sm border border-border bg-jq-surface-1 p-3 font-mono text-[14px]">
          {recoveryCodes.map((code) => (
            <li key={code}>{code}</li>
          ))}
        </ul>
        <Button className="mt-6 w-full" onClick={() => navigate({ to: '/dashboard' })}>
          {t('setupMfa.recovery.continue')}
        </Button>
      </AuthShell>
    );
  }

  const { errors, isSubmitting } = form.formState;

  return (
    <AuthShell
      title={t('setupMfa.title')}
      eyebrow={t('setupMfa.eyebrow')}
      description={mode === 'email' ? t('emailOtp.description') : t('setupMfa.description')}
      aside={{
        illustration: 'padlock',
        eyebrow: t('setupMfa.aside.eyebrow'),
        headline: t('setupMfa.aside.headline'),
        body: t('setupMfa.aside.body'),
      }}
    >
      {mode === 'email' && intent ? (
        <EmailOtpForm
          mfaToken={intent.mfaToken}
          email={intent.email}
          onVerified={async (session) => {
            setSession(session);
            await navigate({ to: '/dashboard' });
          }}
          onBack={() => setMode('setup')}
          backLabel={t('setupMfa.backToSetup')}
        />
      ) : (
        <>
          <ErrorBanner error={error} />
          {qr ? (
            <div className="grid gap-4">
              <div className="flex justify-center">
                <MfaQrCode otpauthUri={qr.otpauthUri} />
              </div>
              <div className="rounded-sm border border-border bg-jq-surface-1 p-3">
                <p className="text-[12px] uppercase tracking-widest text-jq-graphite">
                  {t('setupMfa.secretCodeLabel')}
                </p>
                <p className="mt-1 break-all font-mono text-[13px]">{qr.secret}</p>
              </div>
              <form className="grid gap-4" onSubmit={form.handleSubmit(onSubmit)} noValidate>
                <div className="grid gap-3">
                  <Label htmlFor="code">{t('setupMfa.codeLabel')}</Label>
                  <Controller
                    control={form.control}
                    name="code"
                    render={({ field }) => (
                      <InputOTP
                        id="code"
                        maxLength={OTP_LENGTH}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        pattern="^[0-9]+$"
                        value={field.value}
                        onChange={field.onChange}
                        onComplete={form.handleSubmit(onSubmit)}
                        containerClassName="w-full"
                      >
                        <InputOTPGroup className="w-full gap-2 sm:gap-3">
                          {OTP_SLOT_IDS.map((id, i) => (
                            <InputOTPSlot
                              key={id}
                              index={i}
                              className="h-14 flex-1 text-[22px] sm:h-16"
                            />
                          ))}
                        </InputOTPGroup>
                      </InputOTP>
                    )}
                  />
                  {errors.code ? (
                    <p className="text-[13px] text-jq-danger">{errors.code.message}</p>
                  ) : (
                    <p className="text-[12px] text-jq-graphite">{t('setupMfa.codeTip')}</p>
                  )}
                </div>
                <Button type="submit" disabled={isSubmitting} className="w-full">
                  {isSubmitting ? t('setupMfa.submitting') : t('setupMfa.submit')}
                </Button>
              </form>
            </div>
          ) : (
            <p className="text-[14px] text-jq-graphite">{t('setupMfa.generating')}</p>
          )}
          <Button
            type="button"
            variant="link"
            className="mt-4 justify-self-start px-0"
            onClick={() => setMode('email')}
          >
            {t('setupMfa.skipToEmail')}
          </Button>
        </>
      )}
    </AuthShell>
  );
}
